import React from 'react';
import { Dialog, Button, Typography, Box, IconButton } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { ReactComponent as SettleBillIcon } from '../../../assets/SVG/receipt-edit_2.svg';

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------

const dialogPaperSx = {
  borderRadius: '20px',
  width: 520,
  maxWidth: '92vw',
  p: 0,
  overflow: 'visible',
  boxShadow: '0px 12px 48px rgba(0, 0, 0, 0.14)',
} as const;

const proceedBtnSx = {
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.9rem',
  py: 1.5,
  borderRadius: '12px',
  backgroundColor: '#424242',
  color: '#fff',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#333',
    boxShadow: 'none',
  },
} as const;

const outlinedBtnSx = {
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.9rem',
  py: 1.5,
  borderRadius: '12px',
  borderColor: '#bdbdbd',
  color: '#616161',
  '&:hover': {
    borderColor: '#9e9e9e',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
} as const;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SettleBillModalProps {
  open: boolean;
  onClose: () => void;
  onContinueWithDonor: () => void;
  onContinueWithRecipient: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SettleBillModal: React.FC<SettleBillModalProps> = ({
  open,
  onClose,
  onContinueWithDonor,
  onContinueWithRecipient,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: dialogPaperSx }}
      aria-labelledby="settle-bill-title"
      data-testid="settle-bill-modal"
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        aria-label="Close"
        size="small"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 1,
          width: 30,
          height: 30,
          backgroundColor: '#f5f5f5',
          '&:hover': { backgroundColor: '#e8e8e8' },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 16, color: '#666' }} />
      </IconButton>

      {/* Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 4.5,
          pb: 4,
          px: 4,
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 68,
            height: 68,
            borderRadius: '50%',
            background: 'linear-gradient(145deg, #FFF8EE 0%, #FFEFD5 100%)',
            border: '1px solid #FFE4B8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2.5,
          }}
        >
          <SettleBillIcon style={{ width: 32, height: 32 }} />
        </Box>

        {/* Title */}
        <Typography
          id="settle-bill-title"
          sx={{
            fontWeight: 800,
            fontSize: '1.2rem',
            color: '#1a1a1a',
            mb: 1,
          }}
        >
          Settle Bill
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: '#777',
            textAlign: 'center',
            mb: 4,
            lineHeight: 1.65,
            fontSize: '0.84rem',
          }}
        >
          Please select the user you want to settle this invoice for.
        </Typography>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={onContinueWithDonor}
            sx={outlinedBtnSx}
            data-testid="settle-continue-donor"
          >
            Continue with Donor
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={onContinueWithRecipient}
            sx={proceedBtnSx}
            data-testid="settle-continue-recipient"
          >
            Continue with Recipient
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default SettleBillModal;
